import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Card } from '@/components/Card';
import { CardHeader } from '@/components/CardHeader';
import { EmptyState } from '@/components/EmptyState';
import { ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { formatNumber, formatDate } from '@/lib/locale';
import { apiGetPaginated, qs } from '@/features/_shared/queries';
import { QueryState } from '@/features/_shared/QueryState';

type AggregateReport = {
  id: string;
  reporter_org: string;
  report_id?: string;
  domain_fqdn?: string;
  date_range_begin?: string;
  date_range_end?: string;
  received_at?: string;
  records_count?: number;
};

const PER_PAGE = 20;

export function ReportsBrowser({ slug }: { slug: string }) {
  const [page, setPage] = useState(1);
  const [domainFilter, setDomainFilter] = useState('');
  const [reporterFilter, setReporterFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const params = useMemo(
    () => ({
      tenant: slug,
      page,
      per_page: PER_PAGE,
      // The domain filter input is an FQDN; the backend accepts it via the
      // `domain_fqdn` query param.
      domain_fqdn: domainFilter || undefined,
      reporter_org: reporterFilter || undefined,
      since: from || undefined,
      until: to || undefined,
    }),
    [slug, page, domainFilter, reporterFilter, from, to],
  );

  const reportsQ = useQuery({
    queryKey: ['reports', 'aggregate', params],
    queryFn: () =>
      apiGetPaginated<AggregateReport>(`/reports/aggregate${qs(params)}`, {
        tenant: slug,
      }),
  });

  const rows = reportsQ.data?.data ?? [];
  const total = reportsQ.data?.meta?.total ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="max-w-[1400px] space-y-5 p-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight text-slate-900">
            Aggregate rapporten
          </h1>
          <p className="mt-0.5 text-[13px] text-slate-500">
            {formatNumber(total)} rapporten · pagina {page} / {totalPages}
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap items-end gap-3 px-4 py-3">
          <FilterField
            label="Domein"
            value={domainFilter}
            onChange={(v) => {
              setDomainFilter(v);
              setPage(1);
            }}
            placeholder="bv. mail.example.be"
          />
          <FilterField
            label="Reporter org"
            value={reporterFilter}
            onChange={(v) => {
              setReporterFilter(v);
              setPage(1);
            }}
            placeholder="bv. google.com"
          />
          <FilterField
            label="Van"
            type="date"
            value={from}
            onChange={(v) => {
              setFrom(v);
              setPage(1);
            }}
          />
          <FilterField
            label="Tot"
            type="date"
            value={to}
            onChange={(v) => {
              setTo(v);
              setPage(1);
            }}
          />
          <div className="ml-auto flex items-center gap-1.5 text-[11px] text-slate-500">
            <Filter size={12} />
            Filters
          </div>
        </div>
      </Card>

      <QueryState
        isLoading={reportsQ.isLoading}
        isError={reportsQ.isError}
        onRetry={() => reportsQ.refetch()}
      >
        {rows.length === 0 ? (
          <Card>
            <EmptyState
              title="Geen rapporten in deze periode"
              body="Er zijn geen aggregate reports ontvangen tussen de gekozen data. Pas de periode aan of controleer of de DMARC-record correct gepubliceerd is."
            />
          </Card>
        ) : (
          <Card>
            <CardHeader
              title="Rapporten"
              subtitle={`pagina ${page} van ${totalPages}`}
              right={
                <div className="flex items-center gap-1.5 text-[11px]">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-30"
                    aria-label="Vorige pagina"
                  >
                    <ChevronLeft size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-30"
                    aria-label="Volgende pagina"
                  >
                    <ChevronRight size={12} />
                  </button>
                </div>
              }
            />
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <Th>Reporter</Th>
                    <Th>Domein</Th>
                    <Th>Periode</Th>
                    <Th>Ontvangen</Th>
                    <Th align="right">Records</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <tr key={r.id} className="group cursor-pointer hover:bg-slate-50">
                      <td className="px-3 py-2.5">
                        <Link
                          to="/tenants/$slug/reports/$id"
                          params={{ slug, id: r.id }}
                          className="font-medium text-slate-900 group-hover:text-brand-700"
                        >
                          {r.reporter_org}
                        </Link>
                        {r.report_id && (
                          <div className="font-mono text-[10px] text-slate-400">{r.report_id}</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-slate-700">
                        {r.domain_fqdn ?? '—'}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">
                        {r.date_range_begin ? formatDate(r.date_range_begin) : '—'} –{' '}
                        {r.date_range_end ? formatDate(r.date_range_end) : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">
                        {r.received_at ? formatDate(r.received_at) : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-900">
                        {formatNumber(r.records_count ?? 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </QueryState>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 text-${align}`}
    >
      {children}
    </th>
  );
}

function FilterField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: 'text' | 'date';
}) {
  return (
    <label className="flex flex-col gap-1 text-[11px] text-slate-600">
      <span className="font-semibold uppercase tracking-wider">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-7 rounded border border-slate-200 bg-white px-2 text-[12px] placeholder:text-slate-400 focus:border-slate-400 focus:bg-white focus:outline-none"
      />
    </label>
  );
}
